import React, { useEffect, useState } from 'react';
import { Eye, LogOut, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * The persistent "you are not yourself" bar.
 *
 * God mode signs the app into a real session as someone else, so every number
 * on screen is theirs — and every Save changes THEIR data, as them. That is
 * the point, and it is also why this bar is not dismissible and not subtle. A
 * superadmin who forgets which identity they are acting as would change the
 * wrong person's data, which is the failure this feature was built to prevent
 * rather than cause.
 *
 * It states the identity, the role, that changes are logged (they are, by
 * firestore.rules — a god-mode write without its audit entry is denied), and
 * the time left, because the session has a hard deadline and ending abruptly
 * with no warning would look like a bug.
 */

const ROLE_LABEL = {
  superadmin: 'Super Admin',
  admin: 'Administrator',
  org_owner: 'Organization Owner',
  salesperson: 'Sales Representative',
  affiliate: 'Affiliate',
  social_media: 'Marketing Manager',
  user: 'Organization Manager',
};

const formatRemaining = (ms) => {
  if (ms <= 0) return 'expiring';
  const mins = Math.floor(ms / 60000);
  if (mins >= 1) return `${mins}m left`;
  return `${Math.max(1, Math.floor(ms / 1000))}s left`;
};

const GodModeBanner = () => {
  const { godMode, exitGodMode, userRole, currentUser } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!godMode.active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [godMode.active]);

  if (!godMode.active) return null;

  // The stored target is for the label only. The ROLE shown is the one the app
  // actually loaded from the session, so if those ever disagreed the banner
  // would show the live truth rather than what we hoped we signed into.
  const target = godMode.target || {};
  const name = target.displayName || currentUser?.email || target.uid || 'unknown user';
  const roleLabel = ROLE_LABEL[userRole] || userRole || 'unknown role';
  const remaining = godMode.deadline ? godMode.deadline - now : null;
  const nearlyUp = remaining !== null && remaining < 5 * 60 * 1000;

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitGodMode();
    } finally {
      setExiting(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-amber-500 text-amber-950 shadow-md print:hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 sm:px-6">
        <Eye className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <p className="text-sm font-semibold">
          Acting as {name}
          <span className="ml-1.5 font-normal opacity-80">· {roleLabel}</span>
        </p>
        <span className="hidden text-xs opacity-80 sm:inline">
          Changes you make here are theirs, and every one is logged.
        </span>
        {remaining !== null && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
              nearlyUp ? 'bg-amber-900 text-amber-50' : 'bg-amber-400/60'
            }`}
          >
            {nearlyUp && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
            {formatRemaining(remaining)}
          </span>
        )}
        <button
          onClick={handleExit}
          disabled={exiting}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-amber-950 px-3 py-1.5 text-xs font-semibold text-amber-50 transition hover:bg-amber-900 disabled:opacity-60"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          {exiting ? 'Returning…' : 'Exit god mode'}
        </button>
      </div>
    </div>
  );
};

export default GodModeBanner;
